import { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "../services/mysqlStore";
import { db } from "../services/mysqlStore";
import { ref, uploadBytesResumable, getDownloadURL, storage } from "../services/mysqlStorage";
import { DEFAULT_HOME_CATEGORY_SECTION } from "../constants/homeCategorySection";
import { fetchProducts } from "../services/sinaliteService";

function createCard() {
  return {
    id: `category-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    label: "",
    link: "/products",
    imageUrl: "",
    imageCategoryNames: [],
  };
}

function uploadImage(file, cardId, onProgress) {
  return new Promise((resolve, reject) => {
    const storageRef = ref(storage, `homepage-category-images/${cardId}_${Date.now()}_${file.name}`);
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      "state_changed",
      snapshot => onProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
      reject,
      () => getDownloadURL(task.snapshot.ref).then(resolve).catch(reject)
    );
  });
}

export default function AdminHomeCategorySection() {
  const [section, setSection] = useState(DEFAULT_HOME_CATEGORY_SECTION);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [uploads, setUploads] = useState({});
  const [draggingCardId, setDraggingCardId] = useState("");
  const [productCategories, setProductCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState("");

  useEffect(() => {
    fetchProducts()
      .then(result => setProductCategories(result.categories))
      .catch(loadError => {
        setCategoriesError(loadError.message || "Could not load the current category images.");
      });

    getDoc(doc(db, "siteSettings", "homeCategorySection"))
      .then(snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          setSection({
            ...DEFAULT_HOME_CATEGORY_SECTION,
            ...data,
            cards: Array.isArray(data.cards) ? data.cards : DEFAULT_HOME_CATEGORY_SECTION.cards,
          });
        }
      })
      .catch(() => setError("Could not load homepage product categories."))
      .finally(() => setLoading(false));
  }, []);

  function updateSection(field, value) {
    setSection(current => ({ ...current, [field]: value }));
    setSaved(false);
  }

  function updateCard(cardId, field, value) {
    setSection(current => ({
      ...current,
      cards: current.cards.map(card => card.id === cardId ? { ...card, [field]: value } : card),
    }));
    setSaved(false);
  }

  async function handleImageChange(card, file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Choose an image file.");
      return;
    }
    setError("");
    setUploads(current => ({ ...current, [card.id]: { progress: 0, error: "" } }));
    try {
      const imageUrl = await uploadImage(file, card.id, progress => {
        setUploads(current => ({ ...current, [card.id]: { progress, error: "" } }));
      });
      updateCard(card.id, "imageUrl", imageUrl);
      setUploads(current => ({ ...current, [card.id]: { progress: 100, error: "" } }));
    } catch (uploadError) {
      setUploads(current => ({
        ...current,
        [card.id]: { progress: 0, error: uploadError.message || "Image upload failed." },
      }));
    }
  }

  function handleDrop(event, card) {
    event.preventDefault();
    setDraggingCardId("");
    handleImageChange(card, event.dataTransfer.files[0]);
  }

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      await setDoc(doc(db, "siteSettings", "homeCategorySection"), {
        tag: section.tag,
        title: section.title,
        subtitle: section.subtitle,
        cards: section.cards.map(({ id, label, link, imageUrl, imageCategoryNames }) => ({
          id, label, link, imageUrl: imageUrl || "", imageCategoryNames: imageCategoryNames || [],
        })),
        updatedAt: new Date().toISOString(),
      });
      setSaved(true);
    } catch (saveError) {
      setError(saveError.message || "Could not save homepage product categories.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p style={{ color: "rgba(255,255,255,0.6)" }}>Loading homepage product categories...</p>;

  return (
    <div style={{ maxWidth: 1000 }}>
      <h2 style={{ color: "#fff", margin: "0 0 8px" }}>Homepage Product Categories</h2>
      <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, margin: "0 0 20px" }}>
        Edit the Browse Our Products heading and cards. Each card can have its own image, label, and destination link.
      </p>
      {error && <p role="alert" style={messageStyle("#f87171")}>{error}</p>}
      {saved && <p role="status" style={messageStyle("#4ade80")}>Homepage product categories saved.</p>}

      <section className="adm-card" style={{ padding: 20, marginBottom: 16 }}>
        <Field label="Small heading">
          <input value={section.tag || ""} onChange={event => updateSection("tag", event.target.value)} />
        </Field>
        <Field label="Section title">
          <input value={section.title || ""} onChange={event => updateSection("title", event.target.value)} />
        </Field>
        <Field label="Section description">
          <textarea rows={3} value={section.subtitle || ""} onChange={event => updateSection("subtitle", event.target.value)} />
        </Field>
      </section>

      <div style={{ display: "grid", gap: 12 }}>
        {section.cards.map((card, index) => (
          <section className="adm-card" key={card.id || index} style={{ padding: 16 }}>
            {(() => {
              const matchingCategory = productCategories.find(category => card.imageCategoryNames?.includes(category.name) && category.imageUrl);
              const displayedImage = card.imageUrl || matchingCategory?.imageUrl || "";
              const defaultCard = DEFAULT_HOME_CATEGORY_SECTION.cards.find(item => item.id === card.id);
              return (
                <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <strong style={{ color: "#fff" }}>Card {index + 1}</strong>
              <button type="button" className="adm-btn" onClick={() => updateSection("cards", section.cards.filter(item => item.id !== card.id))}>
                Remove card
              </button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 180px) minmax(0, 1fr)", gap: 16 }}>
              <div>
                {displayedImage
                  ? <img src={displayedImage} alt={`Current image for ${card.label || `card ${index + 1}`}`} style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 8, display: "block", marginBottom: 8 }} />
                  : <div style={{ height: 120, borderRadius: 8, background: "rgba(255,255,255,0.06)", display: "grid", placeItems: "center", color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>No image</div>}
                {displayedImage && <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 11, margin: "0 0 8px" }}>{card.imageUrl ? "Custom card image" : `Using category image${matchingCategory ? `: ${matchingCategory.name}` : ""}`}</p>}
                <div
                  onDragOver={event => { event.preventDefault(); setDraggingCardId(card.id); }}
                  onDragLeave={event => {
                    if (!event.currentTarget.contains(event.relatedTarget)) setDraggingCardId("");
                  }}
                  onDrop={event => handleDrop(event, card)}
                  style={{
                    border: `1px dashed ${draggingCardId === card.id ? "#00aadd" : "rgba(255,255,255,0.3)"}`,
                    borderRadius: 8,
                    padding: 10,
                    textAlign: "center",
                    background: draggingCardId === card.id ? "rgba(0,170,221,0.12)" : "rgba(255,255,255,0.03)",
                  }}
                >
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, margin: "0 0 8px" }}>
                    Drag and drop an image here
                  </p>
                  <label className="adm-btn" style={{ display: "block", textAlign: "center", cursor: "pointer" }}>
                    {uploads[card.id]?.progress > 0 && uploads[card.id]?.progress < 100 ? `Uploading ${uploads[card.id].progress}%` : "Select image"}
                    <input type="file" accept="image/*" disabled={uploads[card.id]?.progress > 0 && uploads[card.id]?.progress < 100} onChange={event => {
                      handleImageChange(card, event.target.files[0]);
                      event.target.value = "";
                    }} style={{ display: "none" }} />
                  </label>
                </div>
                {displayedImage && <button type="button" className="adm-btn" style={{ width: "100%", marginTop: 6 }} onClick={() => {
                  updateCard(card.id, "imageUrl", "");
                  updateCard(card.id, "imageCategoryNames", []);
                }}>Remove image</button>}
                {!displayedImage && defaultCard?.imageCategoryNames?.length > 0 && <button type="button" className="adm-btn" style={{ width: "100%", marginTop: 6 }} onClick={() => updateCard(card.id, "imageCategoryNames", defaultCard.imageCategoryNames)}>Restore category image</button>}
                {uploads[card.id]?.error && <p role="alert" style={messageStyle("#f87171")}>{uploads[card.id].error}</p>}
                {categoriesError && !card.imageUrl && <p style={messageStyle("#fbbf24")}>{categoriesError}</p>}
              </div>
              <div>
                <Field label="Card label">
                  <input value={card.label || ""} onChange={event => updateCard(card.id, "label", event.target.value)} placeholder="e.g. Business Cards" />
                </Field>
                <Field label="Destination link">
                  <input value={card.link || ""} onChange={event => updateCard(card.id, "link", event.target.value)} placeholder="/products?group=Business%20Cards" />
                </Field>
              </div>
            </div>
                </>
              );
            })()}
          </section>
        ))}
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        <button type="button" className="adm-btn" onClick={() => updateSection("cards", [...section.cards, createCard()])}>+ Add category card</button>
        <button type="button" className="adm-btn adm-btn-primary" onClick={handleSave} disabled={saving || Object.values(uploads).some(upload => upload.progress > 0 && upload.progress < 100)}>
          {saving ? "Saving..." : "Save homepage section"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", color: "rgba(255,255,255,0.75)", fontSize: 12, fontWeight: 600, marginBottom: 12 }}>
      {label}
      {children.type === "textarea"
        ? <textarea {...children.props} style={inputStyle} />
        : <input {...children.props} style={inputStyle} />}
    </label>
  );
}

const inputStyle = {
  display: "block",
  width: "100%",
  boxSizing: "border-box",
  marginTop: 6,
  padding: "9px 10px",
  borderRadius: 6,
  border: "1px solid rgba(255,255,255,0.15)",
  background: "rgba(255,255,255,0.05)",
  color: "#fff",
  font: "inherit",
};

function messageStyle(color) {
  return { color, fontSize: 13, margin: "0 0 12px" };
}
